import React, { useEffect, useRef, useState } from 'react'
import { Button, Input } from 'antd'
import Icon from '../Icons/Icons'
import './searchResultItem.less'
import ReactMarkdown from 'react-markdown'
import { EditStorageKnowledgeRecord, StorageKnowledgeRecord } from '../../../types/storage.type'
import { useAppSelector } from '../../../hooks/appHook'
import { RolesEnum } from '../../../config/rolesEnum'
import { useTranslation } from 'react-i18next'

interface Props {
  item: StorageKnowledgeRecord
  handleDeleteItem: (item: StorageKnowledgeRecord) => void
  handleEditData: (value: EditStorageKnowledgeRecord) => void
}

const formatMarkdownLinks = (text: string) => {
  return (
    <ReactMarkdown
      components={{
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {text}
    </ReactMarkdown>
  )
}

const SearchResultItem = ({ item, handleDeleteItem, handleEditData }: Props) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [isDisabled, setIsDisabled] = useState(true)
  const [value, setValue] = useState(item.text)
  const { user } = useAppSelector((state) => state.appState)
  const { t } = useTranslation('components')

  const formattedText = formatMarkdownLinks(value)

  const isEditButtonVisible = !!(item?.metadata.document_id && item?.metadata.chunk_index !== undefined && item.id)

  useEffect(() => {
    setValue(item.text)
  }, [item])

  const handleSetItemEditable = () => {
    setIsDisabled(false)
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleCancelEditing = () => {
    setValue(item.text)
    setIsDisabled(true)
  }

  const onEdit = () => {
    handleEditData({
      value,
      recordId: item.id,
      documentId: item?.metadata.document_id,
      chunkIndex: item?.metadata.chunk_index,
    })

    setIsDisabled(true)
  }

  return (
    <div className="ai-search-data-container">
      {isDisabled ? (
        <>
          <p className="ai-data-paragraph">{formattedText}</p>
          {user?.role === RolesEnum.ADMIN && (
            <pre>
              <div>
                <b>{t('ui.aiSearchResultItem.distance')}</b>
                <span className="ai-data-paragraph-text">{item.distance}</span>
              </div>
              <div>
                <b>{t('ui.aiSearchResultItem.id')}</b>
                <span className="ai-data-paragraph-text">{item.id}</span>
              </div>
              <div>
                <b>{t('ui.aiSearchResultItem.metadata')}</b>
                <span className="ai-data-paragraph-text">{item.metadata ? JSON.stringify(item.metadata) : ''}</span>
              </div>
            </pre>
          )}
        </>
      ) : (
        <Input.TextArea
          autoSize
          ref={inputRef}
          disabled={isDisabled}
          value={value}
          className="ai-data-input"
          onChange={(e) => setValue(e.target.value)}
        />
      )}
      <div className="buttons-container">
        {isEditButtonVisible && (
          <Button
            className="custom-text-button"
            icon={<Icon name="pencil" fill="inherit" />}
            type="link"
            onClick={handleSetItemEditable}
            disabled={!isDisabled}
          >
            <span className="button-text">{t('ui.aiSearchResultItem.edit')}</span>
          </Button>
        )}
        <Button
          onClick={() => handleDeleteItem(item)}
          className="custom-text-button"
          icon={<Icon name="trash" fill="inherit" />}
          type="link"
        >
          <span className="button-text">{t('ui.aiSearchResultItem.delete')}</span>
        </Button>
        {!isDisabled && (
          <>
            <Button className="custom-text-button" icon={<Icon name="synchronization" />} type="link" onClick={onEdit}>
              <span className="button-text">{t('ui.aiSearchResultItem.update')}</span>
            </Button>
            <Button
              onClick={handleCancelEditing}
              className="custom-text-button"
              icon={<Icon name="cross" />}
              type="link"
            >
              <span className="button-text">{t('ui.aiSearchResultItem.cancel')}</span>
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default SearchResultItem
